/**
 * Public Campus landing — lead-gen page accessible without auth.
 * Showcases 10 pillars + 14 use cases + testimonials + CTA "Demander accès".
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
    Sparkles, ArrowRight, Compass, Banknote, TrendingUp, Network, ShieldAlert,
    FileSignature, BarChart3, Presentation, Leaf, Archive, Layers,
    Quote, Award, GraduationCap, Video, Headphones, FileText, FileSpreadsheet,
    CheckCircle2, Bot,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLang } from '@/context/LangContext';
import LangSwitcher from '@/components/LangSwitcher';
import Footer from '@/components/Footer';
import { log } from '@/lib/log';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = {
    Compass, Banknote, TrendingUp, Network, ShieldAlert,
    FileSignature, BarChart3, PresentationChart: Presentation, Leaf, Archive, Layers,
};
const PillarIcon = ({ name, ...rest }) => {
    const Icon = ICON_MAP[name] || Layers;
    return <Icon {...rest} />;
};

const TESTIMONIALS = [
    {
        quote: "Le Core Framework structure mes équipes : chaque chef de projet utilise la même grille de cadrage, le même reporting, les mêmes livrables. La cohérence est totale.",
        author: "Aïcha Diop",
        role: "Directrice Programme · ONG panafricaine",
        location: "Dakar, Sénégal",
    },
    {
        quote: "Nos jeunes cadres financiers maîtrisent désormais le pilotage CAPEX/OPEX et la modélisation cash-flow. Le certificat Tronc Commun fait sa place dans nos parcours RH.",
        author: "Jean-Baptiste Mvondo",
        role: "DAF · Groupe industriel",
        location: "Douala, Cameroun",
    },
    {
        quote: "Les vidéos sectorielles 'Énergie' et 'Mines' ont accéléré l'intégration de mes nouveaux ingénieurs. L'assistant IA répond aux questions techniques 24/7.",
        author: "Sarah K.",
        role: "Project Director · Cabinet ESG",
        location: "Abidjan, Côte d'Ivoire",
    },
];

const VALUE_PROPS = [
    { icon: Video, fr: 'Vidéos pédagogiques', en: 'Pedagogical videos', sub_fr: 'Cours rythmés, sous-titrés, accessibles offline.', sub_en: 'Paced courses, subtitled, offline access.' },
    { icon: Headphones, fr: 'Podcasts experts', en: 'Expert podcasts', sub_fr: 'Écoutez en mobilité — terrain, transports.', sub_en: 'Listen on the go — field, transit.' },
    { icon: FileText, fr: 'Supports PDF imprimables', en: 'Printable PDF materials', sub_fr: 'Guides synthèse + cas pratiques.', sub_en: 'Summary guides + practical cases.' },
    { icon: FileSpreadsheet, fr: 'Modèles Excel africains', en: 'African Excel templates', sub_fr: 'Devises, ratios, normes locales préconfigurés.', sub_en: 'Currencies, ratios, local norms preset.' },
    { icon: CheckCircle2, fr: 'Quiz de validation', en: 'Validation quizzes', sub_fr: 'Seuil 70 % — certificat à la clé.', sub_en: '70% pass — certificate awarded.' },
    { icon: Bot, fr: 'Tuteur IA Campus', en: 'Campus AI tutor', sub_fr: 'Claude 3.5 · réponses contextuelles, exemples africains.', sub_en: 'Claude 3.5 · contextual answers, African examples.' },
];

export default function CampusPublic() {
    const { lang } = useLang();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        import('@/lib/analytics').then((m) => m.trackEvent('campus_viewed'));
        axios.get(`${API}/public/campus/overview`)
            .then(r => setData(r.data))
            .catch(e => log.error('Campus overview load failed', e))
            .finally(() => setLoading(false));
    }, []);

    const pillars = data?.pillars || [];
    const useCases = data?.use_cases || [];
    const stats = data?.stats || { pillars_count: 10, use_cases_count: 14, lessons_count: 0, certificates_issued: 0 };

    return (
        <div className="min-h-screen bg-background flex flex-col" data-testid="public-campus-page">
            {/* Header */}
            <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5" data-testid="brand-header">
                        <img src="/brand/african-valley-logo-256.png" alt="African Valley · Projet 360°"
                             className="h-9 w-9 object-contain rounded" draggable={false} />
                        <div className="leading-tight">
                            <div className="flex items-baseline gap-1.5">
                                <span className="font-heading text-xl">African <span className="text-primary">Valley</span></span>
                            </div>
                            <span className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground">Campus 360°</span>
                        </div>
                    </Link>
                    <div className="flex items-center gap-3">
                        <LangSwitcher />
                        <Link to="/auth/signin" className="hidden sm:inline text-sm text-muted-foreground hover:text-foreground">
                            {lang === 'fr' ? 'Connexion' : 'Sign in'}
                        </Link>
                        <Link to="/apply?source=campus-public" data-testid="public-campus-apply-header">
                            <Button size="sm" className="bg-primary hover:bg-primary-hover">
                                {lang === 'fr' ? 'Demander accès' : 'Request access'} <ArrowRight size={13} className="ml-1.5" />
                            </Button>
                        </Link>
                    </div>
                </div>
            </header>

            <main className="flex-1">
                {/* HERO — full image, no overlay text */}
                <section className="relative overflow-hidden" data-testid="public-campus-hero">
                    <img
                        src="/brand/campus-hero.jpg"
                        alt="Campus African Valley · Projets 360° — Là où les talents africains construisent l'avenir"
                        className="w-full h-auto block select-none"
                        draggable={false}
                    />
                    {/* CTA pill — discretely overlayed bottom-right on desktop */}
                    <div className="absolute bottom-6 right-6 hidden md:flex gap-3">
                        <Link to="/apply?source=campus-public" data-testid="public-campus-apply-hero">
                            <Button size="lg" className="bg-primary hover:bg-primary-hover text-base px-6 shadow-2xl">
                                {lang === 'fr' ? 'Demander accès' : 'Request access'} <ArrowRight size={16} className="ml-2" />
                            </Button>
                        </Link>
                    </div>
                </section>

                {/* Mobile CTA below hero */}
                <div className="md:hidden bg-[#1f1a16] px-6 py-5 flex justify-center">
                    <Link to="/apply?source=campus-public" className="w-full">
                        <Button size="lg" className="bg-primary hover:bg-primary-hover w-full">
                            {lang === 'fr' ? 'Demander accès' : 'Request access'} <ArrowRight size={16} className="ml-2" />
                        </Button>
                    </Link>
                </div>

                {/* STATS BAND — earthy brown, premium frieze under the hero */}
                <section className="relative overflow-hidden text-white"
                         style={{ background: 'linear-gradient(180deg, #4a3528 0%, #3a2820 60%, #2f1f17 100%)' }}
                         data-testid="public-campus-stats">
                    <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
                         style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")" }} />
                    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
                            <Stat n={stats.pillars_count} label={lang === 'fr' ? 'Piliers méthodo' : 'Method pillars'} />
                            <Stat n={stats.use_cases_count} label={lang === 'fr' ? 'Secteurs couverts' : 'Sectors covered'} />
                            <Stat n={stats.lessons_count} label={lang === 'fr' ? 'Leçons publiées' : 'Lessons published'} suffix="+" />
                            <Stat n={stats.certificates_issued} label={lang === 'fr' ? 'Certificats émis' : 'Certificates issued'} suffix="+" />
                        </div>
                        <div className="mt-8 pt-8 border-t border-white/10 flex flex-wrap items-center justify-between gap-4">
                            <div className="text-xs uppercase tracking-[0.25em] text-amber-200/80 inline-flex items-center gap-2">
                                <Sparkles size={12} /> {lang === 'fr' ? 'Apprendre · Innover · Impacter' : 'Learn · Innovate · Impact'}
                            </div>
                            <Link to="/p/diagnostic" className="text-sm text-white/75 hover:text-white inline-flex items-center gap-1.5">
                                {lang === 'fr' ? "Évaluez votre maturité projet (3 min)" : "Assess your project maturity (3 min)"} <ArrowRight size={12} />
                            </Link>
                        </div>
                    </div>
                </section>

                {/* PILIERS */}
                <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="public-campus-pillars">
                    <div className="mb-12 max-w-3xl">
                        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-primary font-medium mb-3">
                            <span className="h-px w-8 bg-primary/40" /> {lang === 'fr' ? 'Le Core Framework' : 'The Core Framework'}
                        </div>
                        <h2 className="font-heading text-4xl lg:text-5xl leading-tight mb-3">
                            {lang === 'fr' ? <>10 piliers pour <span className="text-primary">structurer</span> tout projet</> : <>10 pillars to <span className="text-primary">structure</span> any project</>}
                        </h2>
                        <p className="text-foreground/65 text-lg">
                            {lang === 'fr'
                                ? "Une méthodologie propriétaire « Structurer · Financer · Piloter · Exécuter · Contrôler · Transformer », commune à tous les secteurs."
                                : "A proprietary methodology — Structure · Finance · Steer · Execute · Control · Transform — shared across all sectors."}
                        </p>
                    </div>
                    {loading ? (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            {Array.from({ length: 10 }).map((_, i) => <div key={i} className="h-44 rounded-2xl bg-surface-muted animate-pulse" />)}
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            {pillars.map(p => (
                                <div key={p.slug} className="group relative overflow-hidden rounded-2xl border border-border bg-surface p-5 hover:border-primary/40 hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
                                     data-testid={`public-pillar-${p.slug}`}>
                                    <div className="flex items-start justify-between mb-3">
                                        <div className="h-11 w-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                                             style={{ background: `${p.color_hex}1A`, color: p.color_hex }}>
                                            <PillarIcon name={p.icon} size={20} strokeWidth={1.6} />
                                        </div>
                                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">P{p.order_index}</span>
                                    </div>
                                    <h3 className="font-heading text-lg leading-tight mb-1.5">
                                        {lang === 'fr' ? p.title_fr : p.title_en}
                                    </h3>
                                    <p className="text-xs text-foreground/60 line-clamp-3">
                                        {lang === 'fr' ? p.summary_fr : p.summary_en}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {/* CONTENT FORMATS */}
                <section className="bg-surface-muted/40 border-y border-border">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
                        <div className="mb-12 max-w-3xl">
                            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-secondary font-medium mb-3">
                                <span className="h-px w-8 bg-secondary/40" /> {lang === 'fr' ? "Multimédia immersif" : 'Immersive multimedia'}
                            </div>
                            <h2 className="font-heading text-4xl lg:text-5xl leading-tight mb-3">
                                {lang === 'fr' ? <>Apprendre <span className="text-secondary">comme vous le préférez</span></> : <>Learn <span className="text-secondary">your way</span></>}
                            </h2>
                            <p className="text-foreground/65 text-lg">
                                {lang === 'fr'
                                    ? "Chaque leçon combine plusieurs formats pour s'adapter à votre rythme, votre connexion et votre style d'apprentissage."
                                    : "Each lesson combines multiple formats to fit your pace, connection and learning style."}
                            </p>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {VALUE_PROPS.map((vp, i) => (
                                <div key={i} className="card-soft p-5 hover:border-primary/40 transition-colors">
                                    <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-3">
                                        <vp.icon size={20} strokeWidth={1.5} />
                                    </div>
                                    <h3 className="font-heading text-xl mb-1">{lang === 'fr' ? vp.fr : vp.en}</h3>
                                    <p className="text-sm text-foreground/65">{lang === 'fr' ? vp.sub_fr : vp.sub_en}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* SECTORS */}
                <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="public-campus-sectors">
                    <div className="mb-10 max-w-3xl">
                        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-primary font-medium mb-3">
                            <span className="h-px w-8 bg-primary/40" /> {lang === 'fr' ? "14 verticales sectorielles" : '14 sectoral verticals'}
                        </div>
                        <h2 className="font-heading text-4xl lg:text-5xl leading-tight mb-3">
                            {lang === 'fr' ? <>Conçu pour <span className="text-primary">l'Afrique réelle</span></> : <>Built for <span className="text-primary">real Africa</span></>}
                        </h2>
                        <p className="text-foreground/65 text-lg">
                            {lang === 'fr'
                                ? "Du financement énergétique à la souveraineté agricole, des smart cities aux corridors logistiques."
                                : "From energy financing to agricultural sovereignty, from smart cities to logistics corridors."}
                        </p>
                    </div>
                    {useCases.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {useCases.map(uc => (
                                <span key={uc.slug}
                                      className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-surface border border-border text-sm text-foreground/80 hover:border-primary/40 hover:text-primary transition-colors">
                                    {uc.sector ? `${uc.sector} · ` : ''}{lang === 'fr' ? uc.title_fr : uc.title_en}
                                </span>
                            ))}
                        </div>
                    )}
                </section>

                {/* TESTIMONIALS */}
                <section className="bg-surface-muted/40 border-y border-border">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="public-campus-testimonials">
                        <div className="mb-12 max-w-3xl text-center mx-auto">
                            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-primary font-medium mb-3">
                                <span className="h-px w-8 bg-primary/40" /> {lang === 'fr' ? "Ce qu'en disent les apprenants" : 'What learners say'}
                            </div>
                            <h2 className="font-heading text-4xl lg:text-5xl leading-tight">
                                {lang === 'fr' ? "L'impact se mesure sur le terrain" : 'Impact is measured in the field'}
                            </h2>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6">
                            {TESTIMONIALS.map((t, i) => (
                                <figure key={i} className="card-soft p-6 flex flex-col">
                                    <Quote size={24} className="text-primary/60 mb-3" />
                                    <blockquote className="text-foreground/85 leading-relaxed flex-1 italic">"{t.quote}"</blockquote>
                                    <figcaption className="mt-5 pt-5 border-t border-border">
                                        <div className="font-medium">{t.author}</div>
                                        <div className="text-sm text-muted-foreground">{t.role}</div>
                                        <div className="text-xs text-muted-foreground mt-0.5">{t.location}</div>
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CERTIFICATES BAND */}
                <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
                    <div className="rounded-3xl overflow-hidden relative text-white p-10 lg:p-14"
                         style={{ background: 'linear-gradient(135deg, #2D5F3F 0%, #1f3a2a 100%)' }}>
                        <div className="absolute -top-32 -right-32 h-80 w-80 rounded-full kiosk-aurora opacity-50"
                             style={{ background: 'radial-gradient(closest-side, rgba(200,166,92,0.55), transparent 70%)' }} />
                        <div className="relative grid md:grid-cols-2 gap-10 items-center">
                            <div>
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-[11px] uppercase tracking-[0.3em] text-amber-300 mb-4 ring-1 ring-white/10">
                                    <Award size={12} /> {lang === 'fr' ? 'Certification reconnue' : 'Recognized certification'}
                                </div>
                                <h2 className="font-heading text-4xl lg:text-5xl leading-tight mb-4">
                                    {lang === 'fr' ? "Décrochez votre certificat African Valley" : 'Earn your African Valley certificate'}
                                </h2>
                                <p className="text-white/80 leading-relaxed mb-6">
                                    {lang === 'fr'
                                        ? "Validez vos quiz à 70 % minimum et téléchargez un PDF premium avec QR code vérifiable publiquement — partagez-le sur LinkedIn pour booster votre carrière."
                                        : "Pass quizzes at 70%+ and download a premium PDF with publicly verifiable QR code — share it on LinkedIn to boost your career."}
                                </p>
                                <ul className="space-y-2 text-sm text-white/85">
                                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> {lang === 'fr' ? 'Certificat par Pilier' : 'Per-Pillar certificate'}</li>
                                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> {lang === 'fr' ? 'Certificat Tronc Commun (10 piliers)' : 'Core certificate (10 pillars)'}</li>
                                    <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-amber-300" /> {lang === 'fr' ? 'Certificat sectoriel (Cas d\'usage)' : 'Sectoral certificate (Use case)'}</li>
                                </ul>
                            </div>
                            <div className="relative">
                                <div className="aspect-[1.41/1] rounded-2xl bg-white shadow-2xl rotate-1 hover:rotate-0 transition-transform duration-500 p-8 flex flex-col text-ink">
                                    <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground text-center">★ AFRICAN VALLEY CAMPUS 360° · DISTINCTION PREMIUM ★</div>
                                    <div className="text-center mt-3">
                                        <div className="font-heading text-2xl text-primary leading-none">Certificat de Tronc Commun</div>
                                        <div className="text-xs text-muted-foreground mt-1">African Valley Core Framework · 10 Piliers</div>
                                    </div>
                                    <div className="my-auto text-center">
                                        <div className="text-xs text-muted-foreground">Décerné à</div>
                                        <div className="font-heading text-3xl my-1" style={{ color: '#C8A65C' }}>Aïcha Diop</div>
                                        <div className="text-xs text-muted-foreground">10 leçons · Score moyen <b>92 %</b></div>
                                    </div>
                                    <div className="flex items-end justify-between text-[9px] text-muted-foreground border-t border-stone-200 pt-2">
                                        <span>AVC-CORE-A1B2C3D4-20260515</span>
                                        <div className="h-10 w-10 bg-stone-900 rounded grid place-items-center text-[8px] text-white">QR</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA FINAL */}
                <section className="max-w-4xl mx-auto px-6 lg:px-10 py-20 text-center">
                    <GraduationCap size={48} className="text-primary mx-auto mb-6" strokeWidth={1.2} />
                    <h2 className="font-heading text-4xl lg:text-5xl leading-tight mb-5">
                        {lang === 'fr' ? "Prêt à transformer vos équipes ?" : 'Ready to transform your teams?'}
                    </h2>
                    <p className="text-foreground/65 text-lg mb-10 max-w-2xl mx-auto">
                        {lang === 'fr'
                            ? "Le Campus est inclus dans tous les plans African Valley. Demandez un accès — nous revenons vers vous sous 24h ouvrées."
                            : "Campus is included in all African Valley plans. Request access — we'll reply within 24 business hours."}
                    </p>
                    <div className="flex flex-wrap justify-center items-center gap-4">
                        <Link to="/apply?source=campus-public" data-testid="public-campus-apply-final">
                            <Button size="lg" className="bg-primary hover:bg-primary-hover text-base px-7">
                                {lang === 'fr' ? 'Demander accès au Campus' : 'Request Campus access'} <ArrowRight size={16} className="ml-2" />
                            </Button>
                        </Link>
                        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
                            {lang === 'fr' ? "Découvrir toute la plateforme" : 'Explore the full platform'} <ArrowRight size={12} />
                        </Link>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}

function Stat({ n, label, suffix = '' }) {
    return (
        <div className="text-center md:text-left">
            <div className="font-heading text-5xl lg:text-6xl text-amber-100 tabular-nums leading-none" style={{ textShadow: '0 1px 0 rgba(0,0,0,0.25)' }}>{n}{suffix}</div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-200/70 mt-2.5 font-medium">{label}</div>
        </div>
    );
}
